// Shared client-side table-sort hook (issue #134).
//
// Gives any data table user-sortable column headers without changing its
// default order until the user clicks. Pair with MUI <TableSortLabel> in the
// header cell and render `sortedRows` in the body.
//
//   const { order, orderBy, sortedRows, handleSort } = useTableSort(rows);
//   <TableSortLabel
//     active={orderBy === col}
//     direction={orderBy === col ? order : 'asc'}
//     onClick={() => handleSort(col)} />
//
// Comparison is type-aware: numbers and numeric strings sort numerically,
// dates (DD.MM.YYYY, YYYY-MM-DD, or Date-parsable) sort chronologically,
// everything else sorts as a locale string. Rows keep their original order
// for equal keys (stable) and when no column is selected.

import { useMemo, useState, useCallback } from 'react';

const DMY = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

function toComparable(value) {
  if (value === null || value === undefined || value === '') {
    return { kind: 'empty', v: 0 };
  }
  if (typeof value === 'number') {
    return { kind: 'num', v: value };
  }
  const s = String(value).trim();

  // DD.MM.YYYY (used widely in the explore-stats tables)
  const dmy = s.match(DMY);
  if (dmy) {
    const [, d, m, y] = dmy;
    return { kind: 'num', v: Date.UTC(+y, +m - 1, +d) };
  }

  // Pure number (optionally with thousands separators / %)
  const numLike = s.replace(/[,%\s]/g, '');
  if (numLike !== '' && !Number.isNaN(Number(numLike))) {
    return { kind: 'num', v: Number(numLike) };
  }

  // ISO-ish date
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return { kind: 'num', v: t };
  }

  return { kind: 'str', v: s.toLowerCase() };
}

function compare(a, b) {
  const ca = toComparable(a);
  const cb = toComparable(b);
  // Empty values always sort last regardless of direction-applied later.
  if (ca.kind === 'empty' && cb.kind === 'empty') return 0;
  if (ca.kind === 'empty') return 1;
  if (cb.kind === 'empty') return -1;
  if (ca.kind === 'num' && cb.kind === 'num') return ca.v - cb.v;
  return String(ca.v).localeCompare(String(cb.v), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export default function useTableSort(rows, getCell) {
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState('asc');

  const handleSort = useCallback((column) => {
    setOrderBy((prevBy) => {
      if (prevBy === column) {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return prevBy;
      }
      setOrder('asc');
      return column;
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!orderBy || !Array.isArray(rows)) return rows;
    const cell = getCell || ((row, col) => row[col]);
    // decorate-sort-undecorate keeps the sort stable
    const decorated = rows.map((row, index) => ({ row, index }));
    decorated.sort((x, y) => {
      const c = compare(cell(x.row, orderBy), cell(y.row, orderBy));
      if (c !== 0) return order === 'asc' ? c : -c;
      return x.index - y.index;
    });
    return decorated.map((d) => d.row);
  }, [rows, orderBy, order, getCell]);

  return { order, orderBy, sortedRows, handleSort };
}
